import { Injectable, inject } from '@angular/core';
import {
  ReportingComparisonScopes,
  type ReportFilterOptionsHttpQuery,
  type ReportFilterOptionsReport,
  type ReportFilterOptionsResponseDto,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { catchError, defer, map, of, type Observable } from 'rxjs';
import {
  mapMgaFilterOption,
  mapRmdFilterOption,
  sortReportFilterOptions,
  type ReportFilterOption,
} from '../utils/report-filter-options.utils';
import { ReportingApiService } from './reporting-api.service';

const toOptions = <T>(items: T[] | null | undefined, mapOption: (item: T) => ReportFilterOption | null) =>
  sortReportFilterOptions(
    (Array.isArray(items) ? items : [])
      .map(mapOption)
      .filter((option): option is ReportFilterOption => option !== null),
  );

/**
 * The "Filter by RMD or MGA" options a report page may offer for a scope.
 *
 * The reporting API decides them from the viewer's access, out of the same platform tables the
 * reports are filtered by: company-wide pages list every RMD and MGA, an RMD sees only the MGAs they
 * are the RMD for, and an MGA page has nothing to filter. Other agencies' names never reach a
 * scoped viewer.
 */
@Injectable({ providedIn: 'root' })
export class ReportFilterOptionsService {
  private static readonly path = '/api/reports/filter-options';

  private readonly reportingApiService = inject(ReportingApiService);

  /** `report` is the page asking: the viewer must be allowed that report in the scope. */
  optionsFor(
    scope: ReportingComparisonScope | null,
    report: ReportFilterOptionsReport,
  ): Observable<ReportFilterOption[]> {
    if (scope !== ReportingComparisonScopes.allianceGroup && scope !== ReportingComparisonScopes.rmd) {
      return of([]);
    }

    const query: ReportFilterOptionsHttpQuery = { scope, report };

    return defer(() =>
      this.reportingApiService.request<ReportFilterOptionsResponseDto>(ReportFilterOptionsService.path, {
        method: 'GET',
        query,
      }),
    ).pipe(
      map(response => [
        ...toOptions(response?.rmds, mapRmdFilterOption),
        ...toOptions(response?.agencies, mapMgaFilterOption),
      ]),
      // The page still works without its filter; the report itself says whether it was refused.
      catchError(error => {
        console.warn('[ReportFilterOptions] Failed to load filter options', error);
        return of([]);
      }),
    );
  }
}
